import * as Phaser from "phaser";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";

export class BallPhysicsObject extends Phaser.Physics.Matter.Sprite {
  private scaleXTween?: Phaser.Tweens.Tween;
  private scaleYTween?: Phaser.Tweens.Tween;
  private delay: number = Math.random() * 2000;
  private isGrabbed: boolean = false;
  private grabScaleTween?: Phaser.Tweens.Tween;
  private lastPointerX: number = 0;
  private lastPointerY: number = 0;
  private prevPointerX: number = 0;
  private prevPointerY: number = 0;
  private originalScale: number = 1;
  private grabStartTime: number = 0;
  private grabStartX: number = 0;
  private grabStartY: number = 0;
  private hasMoved: boolean = false;

  public get grabbed(): boolean {
    return this.isGrabbed;
  }

  constructor(private myScene: BasicScene, x: number, y: number) {
    super(
      myScene.matter.world,
      x,
      y,
      myScene.atlas,
      myScene.keys.assets.sprites.items.ball,
    );

    this.setInteractive({ useHandCursor: true, draggable: true });
    this.setCircle(this.width / 2);

    this.setBounce(1);
    this.setFriction(0.01);
    this.setFrictionAir(0.01);

    // ground body
    myScene.matter.add.rectangle(
      myScene.scale.width / 2,
      y + this.height / 2,
      myScene.scale.width,
      10,
      {
        isStatic: true,
        label: "ground",
      },
    );

    this.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.grabBall(pointer);
      // Подписываемся на события сцены для отслеживания движения мыши
      myScene.input.on("pointermove", this.handlePointerMove, this);
      myScene.input.on("pointerup", this.handlePointerUp, this);
    });

    this.scaleXTween = this.myScene.tweens.add({
      targets: this,
      scaleX: 1.03,
      duration: 400,
      ease: "Power2",
      repeat: -1,
      delay: this.delay,
      yoyo: true,
    });

    this.scaleYTween = this.scene.tweens.add({
      targets: this,
      scaleY: 1.03,
      duration: 400,
      ease: "Power2",
      delay: this.delay + 150,
      yoyo: true,
      repeat: -1,
    });
  }

  private grabBall(pointer: Phaser.Input.Pointer): void {
    if (this.isGrabbed) return;

    this.isGrabbed = true;
    // Сохраняем начальную позицию и время
    this.grabStartTime = this.scene.time.now;
    this.grabStartX = pointer.worldX;
    this.grabStartY = pointer.worldY;
    this.lastPointerX = pointer.worldX;
    this.lastPointerY = pointer.worldY;
    this.prevPointerX = pointer.worldX;
    this.prevPointerY = pointer.worldY;
    this.hasMoved = false;

    // Останавливаем постоянные твины масштабирования
    if (this.scaleXTween) {
      this.scaleXTween.pause();
    }
    if (this.scaleYTween) {
      this.scaleYTween.pause();
    }

    // Сохраняем текущий масштаб
    this.originalScale = this.scaleX;

    // Увеличиваем мяч при захвате
    this.grabScaleTween = this.scene.tweens.add({
      targets: this,
      scaleX: this.originalScale * 1.3,
      scaleY: this.originalScale * 1.3,
      duration: 200,
      ease: "Power2",
    });

    // Делаем объект статичным для перемещения
    this.setStatic(true);
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.isGrabbed && pointer.isDown) {
      // Проверяем, двигалась ли мышь достаточно далеко
      const moveDistance = Phaser.Math.Distance.Between(
        this.grabStartX,
        this.grabStartY,
        pointer.worldX,
        pointer.worldY,
      );
      if (moveDistance > 10) {
        this.hasMoved = true;
      }

      // Сохраняем предыдущую позицию перед обновлением
      this.prevPointerX = this.lastPointerX;
      this.prevPointerY = this.lastPointerY;

      // Обновляем текущую позицию
      const currentX = pointer.worldX;
      const currentY = pointer.worldY;
      this.lastPointerX = currentX;
      this.lastPointerY = currentY;

      // Перемещаем мяч за курсором
      this.setPosition(currentX, currentY);
    }
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isGrabbed) {
      this.throwBall(pointer);
      // Отписываемся от событий сцены
      const scene = this.scene as BasicScene;
      scene.input.off("pointermove", this.handlePointerMove, this);
      scene.input.off("pointerup", this.handlePointerUp, this);
    }
  }

  private throwBall(pointer: Phaser.Input.Pointer): void {
    if (!this.isGrabbed) return;

    this.isGrabbed = false;

    // Проверяем, было ли это простым кликом (без движения или с минимальным движением)
    const timeSinceGrab = this.scene.time.now - this.grabStartTime;
    const moveDistance = Phaser.Math.Distance.Between(
      this.grabStartX,
      this.grabStartY,
      pointer.worldX,
      pointer.worldY,
    );
    const isSimpleClick = !this.hasMoved ||
      (moveDistance < 20 && timeSinceGrab < 300);

    // Возвращаем физику
    this.setStatic(false);

    if (isSimpleClick) {
      // Простое нажатие - применяем старую логику отскока
      const randomHorizontal = Phaser.Math.FloatBetween(-20, 20);
      const upwardForce = -35;
      this.setVelocity(randomHorizontal, upwardForce);
    } else {
      // Захват и бросание - вычисляем скорость на основе движения мыши
      const deltaX = pointer.worldX - this.prevPointerX;
      const deltaY = pointer.worldY - this.prevPointerY;

      // Если движение было очень маленьким, даем минимальную скорость
      const minSpeed = 5;
      const maxSpeed = 50; // максимальная скорость броска
      const throwSpeed = 2; // множитель скорости броска

      // Вычисляем направление и скорость
      const speed = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const normalizedSpeed = Math.min(
        Math.max(speed, minSpeed) * throwSpeed,
        maxSpeed,
      );
      const angle = Math.atan2(deltaY, deltaX);

      const velocityX = Math.cos(angle) * normalizedSpeed;
      const velocityY = Math.sin(angle) * normalizedSpeed;

      // Применяем силу броска
      this.setVelocity(velocityX, velocityY);
    }

    // Уменьшаем мяч обратно до нормального размера
    if (this.grabScaleTween) {
      this.grabScaleTween.stop();
      this.grabScaleTween = undefined;
    }

    this.scene.tweens.add({
      targets: this,
      scaleX: this.originalScale,
      scaleY: this.originalScale,
      duration: 200,
      ease: "Power2",
      onComplete: () => {
        // Возобновляем постоянные твины масштабирования
        if (this.scaleXTween) {
          this.scaleXTween.resume();
        }
        if (this.scaleYTween) {
          this.scaleYTween.resume();
        }
      },
    });
  }

  public override destroy(): void {
    // Отписываемся от событий сцены при уничтожении
    if (this.isGrabbed) {
      const scene = this.scene as BasicScene;
      scene.input.off("pointermove", this.handlePointerMove, this);
      scene.input.off("pointerup", this.handlePointerUp, this);
    }

    // Останавливаем твины
    if (this.scaleXTween) {
      this.scaleXTween.stop();
    }
    if (this.scaleYTween) {
      this.scaleYTween.stop();
    }
    if (this.grabScaleTween) {
      this.grabScaleTween.stop();
    }

    super.destroy();
  }
}
